import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2, RotateCcw, Copy, Zap, Plus, X, Check } from 'react-feather';
import { InlineSelect, InlineMultiSelect, InlineInput, InlineCheckbox, BulkActionBar, StatusDot, SortIcon } from '../../components/InlineTable';
import { ConfigToolbar, ToolbarButton, ConfigEmptyState, ConfirmPopover } from '../../components/ConfigPages';
import AiRevenueCoaMappingModal from '../../components/AiRevenueCoaMappingModal';
import { useOptimisticNotifications } from '../../hooks/useOptimisticNotifications';
import {
  updateRevenueType, bulkUpdateRevenueTypes, deleteRevenueType, restoreRevenueType, createRevenueType,
  getRevenueTypeBudgetAccounts, replaceRevenueTypeBudgetAccounts,
} from '../../utils/api';
import {
  SEGMENT_GROUP_OPTIONS, buildCoaOptions, buildCoaMap, coaLabel, mappedTypeOptions, allowCustomMappedType,
  describeRevenueType, useSortConfig, sortRows, useSelection, useSelectionState, bulkGhostBtnStyle, groupBy, isBlank,
} from './revenueMapShared';

// Frozen columns (code-level setting — not user-exposed): checkbox + name.
const FROZEN_LEFTS = [0, 48];
const frozen = (i) => (i >= FROZEN_LEFTS.length
  ? { className: '', left: undefined }
  : { className: ` it-frozen${i === FROZEN_LEFTS.length - 1 ? ' it-frozen-edge' : ''}`, left: FROZEN_LEFTS[i] });

const EMPTY_ADD = { name: '', displayName: '', mappedType: null, chartOfAccountId: null };

/**
 * RevenueTab — the "Revenue" tab of the Revenue map page.
 * Every PMS revenue type → type bucket + COA account (+ budget COAs, segment, classification).
 */
export default function RevenueTab({
  orgId,
  locationId,
  revenueTypes,
  setRevenueTypes,
  chartAccounts,
  budgetMappingsData,
  setBudgetMappingsData,
  typeClassifications,
  showDeleted,
  setShowDeleted,
  statusFilter,
  setStatusFilter,
  refreshRevenueTypes,
  busy = false,
}) {
  const notify = useOptimisticNotifications();
  const [search, setSearch] = useState('');
  const [sortConfig, requestSort] = useSortConfig('name');
  const { selectedIds, toggleOne, toggleMany, clear: clearSelection } = useSelection();
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkNameOpen, setBulkNameOpen] = useState(false);
  const [bulkNameValue, setBulkNameValue] = useState('');
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const [isCopyingCoa, setIsCopyingCoa] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addRow, setAddRow] = useState(EMPTY_ADD);
  const [saving, setSaving] = useState(false);

  const coaOptions = useMemo(() => buildCoaOptions(chartAccounts), [chartAccounts]);
  const coaMap = useMemo(() => buildCoaMap(chartAccounts), [chartAccounts]);
  const classificationOptions = useMemo(() => (typeClassifications || []).map((tc) => ({ value: tc.id, label: tc.name })), [typeClassifications]);

  // ── Derived rows ────────────────────────────────────────────
  const described = useMemo(() => {
    const m = new Map();
    revenueTypes.forEach((t) => m.set(t.id, describeRevenueType(t, coaMap)));
    return m;
  }, [revenueTypes, coaMap]);

  const live = useMemo(() => revenueTypes.filter((t) => !t.isDeleted), [revenueTypes]);
  const counts = useMemo(() => {
    const unmapped = live.filter((t) => described.get(t.id)?.unmapped).length;
    return { all: live.length, unmapped, mapped: live.length - unmapped, deleted: revenueTypes.length - live.length };
  }, [live, revenueTypes.length, described]);

  const rows = useMemo(() => {
    let list = revenueTypes;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((t) => (
        t.name?.toLowerCase().includes(q)
        || t.displayName?.toLowerCase().includes(q)
        || t.mappedType?.toLowerCase().includes(q)
        || (t.chartOfAccountId != null && coaLabel(coaMap, t.chartOfAccountId).toLowerCase().includes(q))
      ));
    }
    if (statusFilter === 'unmapped') list = list.filter((t) => t.isDeleted || described.get(t.id)?.unmapped);
    if (statusFilter === 'mapped') list = list.filter((t) => !t.isDeleted && !described.get(t.id)?.unmapped);
    return sortRows(list, sortConfig, (t, key) => {
      switch (key) {
        case 'chartOfAccountId': return t.chartOfAccountId != null ? coaLabel(coaMap, t.chartOfAccountId) : null;
        case 'typeClassificationId': return typeClassifications.find((tc) => tc.id === t.typeClassificationId)?.name ?? null;
        case 'status': return described.get(t.id)?.unmapped ? 0 : 1;
        default: return t[key] ?? null;
      }
    });
  }, [revenueTypes, search, statusFilter, sortConfig, coaMap, described, typeClassifications]);

  const visibleIds = useMemo(() => rows.filter((t) => !t.isDeleted).map((t) => t.id), [rows]);
  const selState = useSelectionState(visibleIds, selectedIds);

  // ── Field updates ───────────────────────────────────────────
  const patchLocal = useCallback((id, patch) => {
    setRevenueTypes((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, [setRevenueTypes]);

  const updateField = useCallback(async (id, field, rawValue) => {
    let value = rawValue;
    if ((field === 'mappedType' || field === 'segmentGroup' || field === 'displayName') && typeof value === 'string') value = value.trim() || null;
    const original = revenueTypes.find((t) => t.id === id)?.[field];
    patchLocal(id, { [field]: value });
    try {
      await notify.update('Revenue type', updateRevenueType(id, orgId, { [field]: value }), {
        revertAction: () => patchLocal(id, { [field]: original }),
      });
      return true;
    } catch (err) {
      console.error(`Error updating ${field} for revenue type ${id}:`, err);
      throw err;
    }
  }, [revenueTypes, patchLocal, notify, orgId]);

  // Selects fire-and-forget (notify.update already toasts the failure);
  // InlineInput keeps the throwing form so its save indicator turns red.
  const setField = useCallback((id, field, value) => updateField(id, field, value).catch(() => {}), [updateField]);

  // ── Budget COAs (one replace call) ──────────────────────────
  const setBudgetCoas = useCallback(async (revenueTypeId, chartOfAccountIds) => {
    const before = budgetMappingsData[revenueTypeId] || [];
    const beforeIds = before.map((m) => m.chartOfAccountId);
    if (beforeIds.length === chartOfAccountIds.length && beforeIds.every((v) => chartOfAccountIds.includes(v))) return;
    // Optimistic — placeholder rows until the server hands back ids.
    setBudgetMappingsData((prev) => ({
      ...prev,
      [revenueTypeId]: chartOfAccountIds.map((cid) => before.find((m) => m.chartOfAccountId === cid) || { id: `tmp-${cid}`, revenueTypeId, chartOfAccountId: cid }),
    }));
    try {
      const res = await replaceRevenueTypeBudgetAccounts({ orgId, locationId, revenueTypeId, chartOfAccountIds });
      const rowsBack = Array.isArray(res) ? res : (Array.isArray(res?.mappings) ? res.mappings : null);
      const fresh = rowsBack || (await getRevenueTypeBudgetAccounts(orgId, locationId, revenueTypeId)) || [];
      setBudgetMappingsData((prev) => ({ ...prev, [revenueTypeId]: fresh }));
      toast.success('Budget COAs saved');
    } catch (err) {
      console.error('Error replacing budget COAs:', err);
      setBudgetMappingsData((prev) => ({ ...prev, [revenueTypeId]: before }));
      toast.error('Could not save budget COAs — try again.');
    }
  }, [budgetMappingsData, setBudgetMappingsData, orgId, locationId]);

  // ── Copy COA → budget COAs (all live types) ─────────────────
  const copyCoaToBudget = useCallback(async () => {
    setCopyConfirmOpen(false);
    setIsCopyingCoa(true);
    let ok = 0; let skipped = 0; let failed = 0;
    try {
      for (const t of revenueTypes) {
        if (t.isDeleted || t.chartOfAccountId == null) { skipped += 1; continue; }
        try {
          const res = await replaceRevenueTypeBudgetAccounts({ orgId, locationId, revenueTypeId: t.id, chartOfAccountIds: [t.chartOfAccountId] });
          const rowsBack = Array.isArray(res) ? res : (Array.isArray(res?.mappings) ? res.mappings : null);
          setBudgetMappingsData((prev) => ({ ...prev, [t.id]: rowsBack || [{ id: `tmp-${t.id}`, revenueTypeId: t.id, chartOfAccountId: t.chartOfAccountId }] }));
          ok += 1;
        } catch (err) {
          console.error(`Error copying COA for revenue type ${t.id}:`, err);
          failed += 1;
        }
      }
      // Re-sync ids for the placeholders in one fetch.
      try {
        const all = await getRevenueTypeBudgetAccounts(orgId, locationId);
        const grouped = {};
        (all || []).forEach((m) => { (grouped[m.revenueTypeId] = grouped[m.revenueTypeId] || []).push(m); });
        setBudgetMappingsData(grouped);
      } catch (e) { /* placeholders remain until next load */ }
      if (ok > 0) toast.success(`Copied COA to budget COAs on ${ok} revenue type${ok !== 1 ? 's' : ''}`);
      if (failed > 0) toast.error(`${failed} revenue type${failed !== 1 ? 's' : ''} could not be copied — try again.`);
      if (skipped > 0) toast(`Skipped ${skipped} (no COA set or deleted)`);
      if (ok === 0 && failed === 0 && skipped === 0) toast('Nothing to copy');
    } finally {
      setIsCopyingCoa(false);
    }
  }, [revenueTypes, orgId, locationId, setBudgetMappingsData]);

  // ── Bulk actions ────────────────────────────────────────────
  const bulkSet = useCallback(async (field, value, successMsg) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('No rows selected'); return; }
    const originals = ids.map((id) => ({ id, value: revenueTypes.find((t) => t.id === id)?.[field] }));
    setRevenueTypes((prev) => prev.map((t) => (selectedIds.has(t.id) ? { ...t, [field]: value } : t)));
    setIsBulkUpdating(true);
    try {
      await bulkUpdateRevenueTypes(orgId, locationId, ids, field, value);
      toast.success(successMsg(ids.length));
      clearSelection();
    } catch (err) {
      console.error(`Error bulk updating ${field}:`, err);
      toast.error(`Could not update ${ids.length} revenue type${ids.length !== 1 ? 's' : ''} — try again.`);
      setRevenueTypes((prev) => prev.map((t) => {
        const o = originals.find((x) => x.id === t.id);
        return o ? { ...t, [field]: o.value } : t;
      }));
    } finally {
      setIsBulkUpdating(false);
    }
  }, [selectedIds, revenueTypes, setRevenueTypes, orgId, locationId, clearSelection]);

  const bulkClassify = (typeClassificationId) => {
    const name = classificationOptions.find((o) => o.value === typeClassificationId)?.label || 'None';
    return bulkSet('typeClassificationId', typeClassificationId ?? null, (n) => `Set classification "${name}" on ${n} revenue type${n !== 1 ? 's' : ''}`);
  };
  const bulkDisplayName = async () => {
    const v = bulkNameValue.trim() || null;
    setBulkNameOpen(false);
    await bulkSet('displayName', v, (n) => (v ? `Set display name "${v}" on ${n} revenue type${n !== 1 ? 's' : ''}` : `Cleared display name on ${n} revenue type${n !== 1 ? 's' : ''}`));
    setBulkNameValue('');
  };

  // ── AI apply: one bulk call per distinct account ────────────
  const applyAiMappings = useCallback(async (mappings) => {
    setAiOpen(false);
    const usable = (mappings || []).filter((m) => m.chartOfAccountId != null);
    if (usable.length === 0) { toast('No mappings selected'); return; }
    const groups = groupBy(usable, (m) => m.chartOfAccountId);
    const toastId = toast.loading(`Applying ${usable.length} COA mapping${usable.length !== 1 ? 's' : ''}…`);
    let applied = 0; let failed = 0;
    for (const [chartOfAccountId, items] of groups) {
      const ids = items.map((m) => m.revenueTypeId);
      try {
        await bulkUpdateRevenueTypes(orgId, locationId, ids, 'chartOfAccountId', chartOfAccountId);
        applied += ids.length;
        setRevenueTypes((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, chartOfAccountId } : t)));
      } catch (err) {
        console.error('Error applying AI COA mappings:', err);
        failed += ids.length;
      }
    }
    toast.dismiss(toastId);
    if (applied > 0) toast.success(`Applied ${applied} COA mapping${applied !== 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`${failed} mapping${failed !== 1 ? 's' : ''} failed — try again.`);
    if (refreshRevenueTypes) refreshRevenueTypes();
  }, [orgId, locationId, setRevenueTypes, refreshRevenueTypes]);

  // ── Delete / restore ────────────────────────────────────────
  const confirmDelete = async () => {
    const t = revenueTypes.find((x) => x.id === deleteId);
    if (!t) return;
    setDeleting(true);
    try {
      await deleteRevenueType(t.id);
      toast.success(`Deleted "${t.name}"`);
      setDeleteId(null);
      if (refreshRevenueTypes) await refreshRevenueTypes();
    } catch (err) {
      console.error('Error deleting revenue type:', err);
      toast.error(`Could not delete "${t.name}" — try again.`);
    } finally {
      setDeleting(false);
    }
  };
  const restore = async (t) => {
    try {
      await restoreRevenueType(t.id, orgId);
      toast.success(`Restored "${t.name}"`);
      if (refreshRevenueTypes) await refreshRevenueTypes();
    } catch (err) {
      console.error('Error restoring revenue type:', err);
      toast.error(`Could not restore "${t.name}" — try again.`);
    }
  };

  // ── Add row ─────────────────────────────────────────────────
  const saveAdd = async () => {
    const name = addRow.name.trim();
    if (!name) { toast.error('Give the revenue type a name'); return; }
    setSaving(true);
    try {
      const created = await createRevenueType({
        orgId, locationId, name,
        displayName: addRow.displayName.trim() || null,
        mappedType: addRow.mappedType || null,
        chartOfAccountId: addRow.chartOfAccountId ?? null,
      });
      const row = created && created.id ? created : { id: created?.id, orgId, locationId, name, ...addRow, isDeleted: false };
      setRevenueTypes((prev) => [row, ...prev]);
      toast.success(`Added "${name}"`);
      setAddRow(EMPTY_ADD);
      setAdding(false);
      if (!created?.id && refreshRevenueTypes) refreshRevenueTypes();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error(`A revenue type named ${name} already exists here`);
      else toast.error(err?.response?.data?.error || err?.response?.data?.message || `Could not add "${name}" — try again.`);
    } finally {
      setSaving(false);
    }
  };

  const landsIn = (t) => {
    const acct = t.chartOfAccountId != null ? coaMap.get(t.chartOfAccountId) : null;
    if (!acct) return null;
    const dept = acct.department || 'No department';
    return <div className="cp-lands">lands in <strong>{dept}{!isBlank(t.mappedType) ? ` · ${t.mappedType}` : ''}</strong></div>;
  };

  const th = (key, label, extra = {}) => (
    <th className={`it-sort${extra.className || ''}`} style={extra.style} onClick={() => requestSort(key)} title={extra.title}>
      {label}<SortIcon columnKey={key} sortKey={sortConfig.key} direction={sortConfig.direction} />
    </th>
  );

  return (
    <div className="it-frame">
      <ConfigToolbar
        search={{ value: search, onChange: setSearch, placeholder: 'Search name, type or account…' }}
        status={{ value: statusFilter, counts, onChange: setStatusFilter }}
        showDeleted={{ value: showDeleted, onChange: setShowDeleted, count: showDeleted ? counts.deleted : undefined }}
        meta={`${rows.length} of ${counts.all}`}
        actions={(
          <>
            <ToolbarButton variant="ai" icon={Zap} onClick={() => setAiOpen(true)} disabled={busy || live.length === 0 || chartAccounts.length === 0} title="Let AI suggest a COA account for each revenue type">
              AI map COA
            </ToolbarButton>
            <ToolbarButton variant="ghost" icon={Copy} onClick={() => setCopyConfirmOpen(true)} disabled={busy || isCopyingCoa} title="Copy each type's COA into its budget COAs">
              {isCopyingCoa ? 'Copying…' : 'Copy COA to budget'}
            </ToolbarButton>
            <ToolbarButton variant="primary" icon={Plus} onClick={() => { setAdding(true); setAddRow(EMPTY_ADD); }} disabled={adding}>
              Add revenue type
            </ToolbarButton>
          </>
        )}
      />

      {selectedIds.size > 0 && (
        <BulkActionBar
          count={selectedIds.size}
          noun={`selected`}
          actionLabel="Set classification:"
          options={classificationOptions}
          placeholder="Choose…"
          allowEmptyApply
          applying={isBulkUpdating}
          onApply={(value) => bulkClassify(value === '' || value == null ? null : Number(value))}
          onClear={clearSelection}
        >
          <button type="button" onClick={() => { setBulkNameValue(''); setBulkNameOpen(true); }} disabled={isBulkUpdating} style={bulkGhostBtnStyle(isBulkUpdating)}>
            Set display name…
          </button>
        </BulkActionBar>
      )}

      <div className="it-scroll">
        <table className="it-tbl" style={{ minWidth: 1900, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 48 }} />
            <col style={{ width: 250 }} />
            <col style={{ width: 210 }} />
            <col style={{ width: 170 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 320 }} />
            <col style={{ width: 340 }} />
            <col style={{ width: 230 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 96 }} />
          </colgroup>
          <thead>
            <tr>
              <th className={`text-center${frozen(0).className}`} style={{ left: frozen(0).left }}>
                <InlineCheckbox checked={selState.all} indeterminate={selState.some} onChange={() => toggleMany(visibleIds)} disabled={busy || visibleIds.length === 0} title={`Select all ${visibleIds.length} visible`} />
              </th>
              {th('name', 'Revenue type', { className: frozen(1).className, style: { left: frozen(1).left } })}
              {th('displayName', 'Display name')}
              {th('mappedType', 'Type', { title: 'Type bucket the dashboards roll this revenue into' })}
              {th('segmentGroup', 'Segment', { title: 'Forecast segment this type’s actuals belong to' })}
              {th('chartOfAccountId', 'COA account')}
              <th>Budget COAs</th>
              {th('typeClassificationId', 'Classification')}
              {th('status', 'Status', { style: { textAlign: 'center' } })}
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {adding && (
              <tr className="cp-row-new">
                <td className={`text-center${frozen(0).className}`} style={{ left: frozen(0).left }} />
                <td className={frozen(1).className} style={{ left: frozen(1).left }}>
                  <div className="cp-cell">
                    <input className="cp-input" style={{ height: 30, width: '100%' }} autoFocus placeholder="Name (as the PMS sends it)" value={addRow.name}
                      onChange={(e) => setAddRow((r) => ({ ...r, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveAdd(); if (e.key === 'Escape') setAdding(false); }} />
                  </div>
                </td>
                <td>
                  <div className="cp-cell">
                    <input className="cp-input" style={{ height: 30, width: '100%' }} placeholder="Display name (optional)" value={addRow.displayName}
                      onChange={(e) => setAddRow((r) => ({ ...r, displayName: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveAdd(); if (e.key === 'Escape') setAdding(false); }} />
                  </div>
                </td>
                <td>
                  <InlineSelect value={addRow.mappedType} options={mappedTypeOptions(addRow.mappedType)} onChange={(v) => setAddRow((r) => ({ ...r, mappedType: v }))}
                    onCreateOption={allowCustomMappedType ? (text) => text.trim() : undefined} createLabel="Use custom type" placeholder="Type…" rowActive minWidth={200} ariaLabel="Type for new revenue type" />
                </td>
                <td><span className="cp-cell cp-mono" style={{ color: 'var(--lodge-onyx-400)' }}>—</span></td>
                <td>
                  <InlineSelect value={addRow.chartOfAccountId} options={coaOptions} onChange={(v) => setAddRow((r) => ({ ...r, chartOfAccountId: v }))} placeholder="COA account…" rowActive minWidth={300} ariaLabel="COA account for new revenue type" />
                </td>
                <td colSpan={3}><div className="cp-help" style={{ padding: '0 12px' }}>Budget COAs, classification and segment can be set once the type exists.</div></td>
                <td style={{ textAlign: 'right' }}>
                  <div className="cp-cell" style={{ display: 'inline-flex', gap: 4 }}>
                    <ToolbarButton small variant="primary" icon={Check} onClick={saveAdd} disabled={saving || !addRow.name.trim()}>{saving ? 'Saving…' : 'Save'}</ToolbarButton>
                    <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={() => setAdding(false)} aria-label="Cancel" title="Cancel" />
                  </div>
                </td>
              </tr>
            )}
            {rows.map((t) => {
              const d = described.get(t.id) || {};
              const sel = selectedIds.has(t.id);
              const disabled = busy || !!t.isDeleted;
              const budgetValues = (budgetMappingsData[t.id] || []).map((m) => coaOptions.find((o) => o.value === m.chartOfAccountId) || { value: m.chartOfAccountId, label: `Account ${m.chartOfAccountId}` });
              return (
                <tr key={t.id} className={`cp-row${sel ? ' it-row-selected' : ''}${t.isDeleted ? ' it-row-deleted' : ' it-row-hover'}`}>
                  <td className={`text-center${frozen(0).className}`} style={{ left: frozen(0).left }}>
                    <InlineCheckbox checked={sel} onChange={() => toggleOne(t.id)} disabled={disabled} />
                  </td>
                  <td className={frozen(1).className} style={{ left: frozen(1).left }}>
                    <div className="cp-cell">
                      <div className="cp-name" title={t.name || ''} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name || '—'}</div>
                      {t.isDeleted ? <div className="cp-code">Deleted</div> : null}
                    </div>
                  </td>
                  <td>
                    <InlineInput value={t.displayName || ''} onChange={(v) => updateField(t.id, 'displayName', v)} placeholder="Display name" rowActive disabled={disabled} debounceMs={700} ariaLabel={`Display name for ${t.name}`} />
                  </td>
                  <td>
                    <InlineSelect
                      value={isBlank(t.mappedType) ? null : t.mappedType}
                      options={mappedTypeOptions(t.mappedType)}
                      onChange={(v) => setField(t.id, 'mappedType', v ?? null)}
                      onCreateOption={allowCustomMappedType ? (text) => text.trim() : undefined}
                      createLabel="Use custom type"
                      placeholder="Type…" rowActive disabled={disabled} minWidth={200}
                      ariaLabel={`Type for ${t.name}`}
                    />
                  </td>
                  <td>
                    <InlineSelect value={t.segmentGroup || null} options={SEGMENT_GROUP_OPTIONS} onChange={(v) => setField(t.id, 'segmentGroup', v ?? null)} placeholder="Segment…" rowActive clearable disabled={disabled} minWidth={160} ariaLabel={`Forecast segment for ${t.name}`} />
                  </td>
                  <td>
                    <InlineSelect value={t.chartOfAccountId ?? null} options={coaOptions} onChange={(v) => setField(t.id, 'chartOfAccountId', v ?? null)} placeholder="Select account…" rowActive disabled={disabled} minWidth={300} ariaLabel={`COA account for ${t.name}`} />
                    {landsIn(t)}
                  </td>
                  <td>
                    <InlineMultiSelect options={coaOptions} value={budgetValues} onChange={(opts) => setBudgetCoas(t.id, (opts || []).map((o) => o.value))} placeholder="Budget COAs…" rowActive disabled={disabled} minWidth={420} ariaLabel={`Budget COAs for ${t.name}`} />
                  </td>
                  <td>
                    <InlineSelect value={t.typeClassificationId ?? null} options={classificationOptions} onChange={(v) => setField(t.id, 'typeClassificationId', v ?? null)} placeholder="Classify…" rowActive disabled={disabled} minWidth={260} ariaLabel={`Classification for ${t.name}`} />
                  </td>
                  <td><StatusDot state={d.state} title={d.title} /></td>
                  <td style={{ textAlign: 'right' }}>
                    <div className="cp-cell" style={{ position: 'relative', display: 'inline-block' }}>
                      {t.isDeleted ? (
                        <ToolbarButton small variant="ghost" icon={RotateCcw} onClick={() => restore(t)} title="Restore this revenue type">Restore</ToolbarButton>
                      ) : (
                        <div className="cp-row-actions">
                          <ToolbarButton small iconOnly variant="ghost" icon={Trash2} onClick={() => setDeleteId(t.id)} aria-label={`Delete ${t.name}`} title="Delete revenue type" />
                        </div>
                      )}
                      <ConfirmPopover open={deleteId === t.id} onClose={() => setDeleteId(null)} onConfirm={confirmDelete} title={`Delete "${t.name}"?`} body="It stops feeding dashboards and the P&L; you can restore it from Show deleted." confirmLabel="Delete" busy={deleting} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {revenueTypes.length === 0 && !busy && (
          <ConfigEmptyState title="No revenue types here yet" body="They arrive with the first PMS revenue file for this location, or add one by hand." action={<ToolbarButton variant="primary" icon={Plus} onClick={() => setAdding(true)}>Add revenue type</ToolbarButton>} />
        )}
        {revenueTypes.length > 0 && rows.length === 0 && (
          <ConfigEmptyState title="Nothing matches" body={statusFilter !== 'all' ? `No ${statusFilter} revenue types match this search.` : 'No revenue types match this search.'} action={<ToolbarButton variant="ghost" onClick={() => { setSearch(''); setStatusFilter('all'); }}>Clear filters</ToolbarButton>} />
        )}
      </div>

      {bulkNameOpen && (
        <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setBulkNameOpen(false); }}>
          <div className="cp-modal" style={{ maxWidth: 480 }} role="dialog" aria-label="Set display name">
            <div className="cp-modal-head"><h2>Set display name</h2><ToolbarButton small iconOnly variant="ghost" icon={X} onClick={() => setBulkNameOpen(false)} aria-label="Close" /></div>
            <div className="cp-modal-body">
              <p className="cp-help" style={{ marginBottom: 12 }}>Applies one shared display name to the {selectedIds.size} selected revenue type{selectedIds.size !== 1 ? 's' : ''} so they roll up together in reporting.</p>
              <div className="cp-field">
                <label className="cp-label" htmlFor="rm-bulk-name">Display name</label>
                <input id="rm-bulk-name" className="cp-input" autoFocus value={bulkNameValue} onChange={(e) => setBulkNameValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') bulkDisplayName(); }} placeholder="Leave empty to clear" />
                <div className="cp-help">Empty + Apply clears the display name on the selected rows.</div>
              </div>
            </div>
            <div className="cp-modal-foot">
              <ToolbarButton variant="ghost" onClick={() => setBulkNameOpen(false)}>Cancel</ToolbarButton>
              <ToolbarButton variant="primary" onClick={bulkDisplayName} disabled={isBulkUpdating}>Apply</ToolbarButton>
            </div>
          </div>
        </div>
      )}

      {copyConfirmOpen && (
        <div className="cp-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) setCopyConfirmOpen(false); }}>
          <div className="cp-modal" style={{ maxWidth: 480 }} role="dialog" aria-label="Copy COA to budget COAs">
            <div className="cp-modal-head"><h2>Copy COA to budget COAs?</h2><ToolbarButton small iconOnly variant="ghost" icon={X} onClick={() => setCopyConfirmOpen(false)} aria-label="Close" /></div>
            <div className="cp-modal-body">
              <p style={{ fontSize: 13, color: 'var(--lodge-onyx-700)', margin: '0 0 10px' }}>Copies each revenue type's <strong>COA account</strong> into its <strong>Budget COAs</strong> for this location.</p>
              <div className="cp-note cp-warn">This overwrites any existing budget COAs here and cannot be undone.</div>
            </div>
            <div className="cp-modal-foot">
              <ToolbarButton variant="ghost" onClick={() => setCopyConfirmOpen(false)}>Cancel</ToolbarButton>
              <ToolbarButton variant="primary" icon={Copy} onClick={copyCoaToBudget}>Copy</ToolbarButton>
            </div>
          </div>
        </div>
      )}

      <AiRevenueCoaMappingModal
        isOpen={aiOpen}
        onClose={() => setAiOpen(false)}
        revenueTypes={live}
        chartAccounts={chartAccounts}
        orgId={orgId}
        locationId={locationId}
        onApplyMappings={applyAiMappings}
      />
    </div>
  );
}
